<template>
  <div class="rating-form-section">
    <div class="rating-header">
      <h2>Évaluez votre voyage</h2>
      <p class="rating-subtitle">Votre avis nous aide à améliorer nos services</p>
    </div>

    <div v-if="!submitted" class="card">
      <div class="card-content">
        <!-- Note globale (obligatoire) -->
        <div class="rating-group">
          <label class="rating-label required">Note globale</label>
          <div class="stars-input">
            <button
              v-for="star in 5"
              :key="'overall-' + star"
              @click="formData.overallRating = star"
              type="button"
              class="star-btn"
              :class="{ active: star <= (formData.overallRating || 0) }"
            >
              {{ star <= (formData.overallRating || 0) ? '⭐' : '☆' }}
            </button>
          </div>
          <span v-if="errors.overallRating" class="error-text">{{ errors.overallRating }}</span>
        </div>

        <!-- Notes détaillées (optionnelles) -->
        <div class="optional-ratings">
          <p class="optional-label">Évaluez des aspects spécifiques (optionnel)</p>

          <!-- Ponctualité -->
          <div class="rating-group">
            <label class="rating-label">Ponctualité</label>
            <div class="stars-input">
              <button
                v-for="star in 5"
                :key="'punctuality-' + star"
                @click="formData.punctualityRating = star"
                type="button"
                class="star-btn small"
                :class="{ active: star <= (formData.punctualityRating || 0) }"
              >
                {{ star <= (formData.punctualityRating || 0) ? '⭐' : '☆' }}
              </button>
              <button
                v-if="formData.punctualityRating"
                @click="formData.punctualityRating = null"
                type="button"
                class="clear-btn"
              >
                ✕
              </button>
            </div>
          </div>

          <!-- Confort -->
          <div class="rating-group">
            <label class="rating-label">Confort</label>
            <div class="stars-input">
              <button
                v-for="star in 5"
                :key="'comfort-' + star"
                @click="formData.comfortRating = star"
                type="button"
                class="star-btn small"
                :class="{ active: star <= (formData.comfortRating || 0) }"
              >
                {{ star <= (formData.comfortRating || 0) ? '⭐' : '☆' }}
              </button>
              <button
                v-if="formData.comfortRating"
                @click="formData.comfortRating = null"
                type="button"
                class="clear-btn"
              >
                ✕
              </button>
            </div>
          </div>

          <!-- Propreté -->
          <div class="rating-group">
            <label class="rating-label">Propreté</label>
            <div class="stars-input">
              <button
                v-for="star in 5"
                :key="'cleanliness-' + star"
                @click="formData.cleanlinessRating = star"
                type="button"
                class="star-btn small"
                :class="{ active: star <= (formData.cleanlinessRating || 0) }"
              >
                {{ star <= (formData.cleanlinessRating || 0) ? '⭐' : '☆' }}
              </button>
              <button
                v-if="formData.cleanlinessRating"
                @click="formData.cleanlinessRating = null"
                type="button"
                class="clear-btn"
              >
                ✕
              </button>
            </div>
          </div>

          <!-- Chauffeur (si applicable) -->
          <div v-if="hasDriver" class="rating-group">
            <label class="rating-label">Chauffeur</label>
            <div class="stars-input">
              <button
                v-for="star in 5"
                :key="'driver-' + star"
                @click="formData.driverRating = star"
                type="button"
                class="star-btn small"
                :class="{ active: star <= (formData.driverRating || 0) }"
              >
                {{ star <= (formData.driverRating || 0) ? '⭐' : '☆' }}
              </button>
              <button
                v-if="formData.driverRating"
                @click="formData.driverRating = null"
                type="button"
                class="clear-btn"
              >
                ✕
              </button>
            </div>
          </div>

          <!-- Rapport qualité/prix -->
          <div class="rating-group">
            <label class="rating-label">Rapport qualité/prix</label>
            <div class="stars-input">
              <button
                v-for="star in 5"
                :key="'value-' + star"
                @click="formData.valueForMoneyRating = star"
                type="button"
                class="star-btn small"
                :class="{ active: star <= (formData.valueForMoneyRating || 0) }"
              >
                {{ star <= (formData.valueForMoneyRating || 0) ? '⭐' : '☆' }}
              </button>
              <button
                v-if="formData.valueForMoneyRating"
                @click="formData.valueForMoneyRating = null"
                type="button"
                class="clear-btn"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        <!-- Commentaire -->
        <div class="rating-group">
          <label class="rating-label">Commentaire (optionnel)</label>
          <textarea
            v-model="formData.reviewText"
            placeholder="Partagez votre expérience..."
            class="textarea-field"
            rows="4"
            maxlength="1000"
          ></textarea>
          <span class="char-count">{{ formData.reviewText?.length || 0 }}/1000</span>
        </div>

        <!-- Bouton de soumission -->
        <div class="submit-section">
          <button
            @click="submitRating"
            :disabled="submitting || !formData.overallRating"
            class="btn btn-primary"
            type="button"
          >
            {{ submitting ? 'Envoi en cours...' : 'Envoyer mon avis' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Message de confirmation -->
    <div v-else class="success-message card">
      <div class="card-content center">
        <div class="success-icon">✅</div>
        <h3>Merci pour votre avis !</h3>
        <p>Votre évaluation a été enregistrée avec succès.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'  // ← Enlevez onMounted
import { useToast } from 'vue-toast-notification'
import { config } from '@/config/env.js'

const props = defineProps({
  tripUuid: {
    type: String,
    required: true
  },
  companyId: {
    type: Number,
    required: true
  },
  driverId: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['rating-submitted'])

const toast = useToast()
const submitting = ref(false)
const submitted = ref(false)

const formData = reactive({
  overallRating: 0,
  punctualityRating: null,
  comfortRating: null,
  cleanlinessRating: null,
  driverRating: null,
  valueForMoneyRating: null,
  reviewText: ''
})

const errors = reactive({
  overallRating: ''
})

const hasDriver = computed(() => props.driverId !== null)

const validate = () => {
  errors.overallRating = ''

  if (!formData.overallRating || formData.overallRating < 1) {
    errors.overallRating = 'Veuillez donner une note globale'
    return false
  }

  return true
}

const submitRating = async () => {
  if (!validate()) {
    toast.error('Veuillez donner une note globale')
    return
  }

  submitting.value = true

  try {
    const payload = {
      tripUuid: props.tripUuid,
      companyId: props.companyId,
      driverId: props.driverId,
      overallRating: formData.overallRating,
      punctualityRating: formData.punctualityRating,
      comfortRating: formData.comfortRating,
      cleanlinessRating: formData.cleanlinessRating,
      driverRating: formData.driverRating,
      valueForMoneyRating: formData.valueForMoneyRating,
      reviewText: formData.reviewText || null,
      isVerified: true
    }

    console.log('📤 Sending:', payload)

    const response = await fetch(`${config.apiUrl}/ratings`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    console.log('📡 Status:', response.status)

    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      let errorMessage = 'Erreur lors de l\'envoi de l\'avis'

      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json()
        errorMessage = errorData.error || errorData.message || errorMessage
      } else {
        const text = await response.text()
        console.error('Response HTML:', text.substring(0, 200))
        errorMessage = `Erreur ${response.status}`
      }

      throw new Error(errorMessage)
    }

    const result = await response.json()
    console.log('✅ Success:', result)

    submitted.value = true
    toast.success('Votre avis a été envoyé avec succès !')
    emit('rating-submitted')

  } catch (err) {
    console.error('❌ Error:', err)
    toast.error(err.message || 'Erreur lors de l\'envoi de l\'avis')
  } finally {
    submitting.value = false
  }
}
</script>

<!-- Le template et style restent identiques -->

<style scoped>
.rating-form-section {
  width: 100%;
}

.rating-header {
  margin-bottom: 1.5rem;
}

.rating-header h2 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--color-text-primary);
}

.rating-subtitle {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
}

.rating-group {
  margin-bottom: 1.5rem;
}

.rating-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--color-text-primary);
  font-size: 1rem;
}

.rating-label.required::after {
  content: ' *';
  color: var(--color-danger, #dc3545);
}

.stars-input {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.star-btn {
  appearance: none;
  border: none;
  background: transparent;
  font-size: 2.5rem;
  cursor: pointer;
  transition: transform 0.2s;
  padding: 0;
  line-height: 1;
}

.star-btn.small {
  font-size: 1.8rem;
}

.star-btn:hover {
  transform: scale(1.15);
}

.star-btn.active {
  animation: pop 0.3s ease;
}

@keyframes pop {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

.clear-btn {
  appearance: none;
  border: none;
  background: var(--color-grey-light);
  color: var(--color-text-secondary);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
  margin-left: 0.5rem;
}

.clear-btn:hover {
  background: var(--color-danger, #dc3545);
  color: white;
}

.optional-ratings {
  border-top: 1px solid var(--color-border);
  padding-top: 1.5rem;
  margin-top: 1.5rem;
}

.optional-label {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin-bottom: 1rem;
  font-style: italic;
}

.textarea-field {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-sm);
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
}

.textarea-field:focus {
  outline: none;
  border-color: var(--color-primary);
}

.char-count {
  display: block;
  text-align: right;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  margin-top: 0.25rem;
}

.error-text {
  display: block;
  color: var(--color-danger, #dc3545);
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.submit-section {
  margin-top: 2rem;
  text-align: center;
}

.success-message {
  text-align: center;
  padding: 3rem 2rem;
}

.success-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.success-message h3 {
  color: var(--color-success, #28a745);
  margin-bottom: 0.5rem;
}

.success-message p {
  color: var(--color-text-secondary);
}

.center {
  text-align: center;
}

@media (max-width: 768px) {
  .star-btn {
    font-size: 2rem;
  }

  .star-btn.small {
    font-size: 1.5rem;
  }
}
</style>
